import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';

// Reads website content from Supabase and returns it in the same shapes the pages used before
// (so the page layouts did not have to change). Each collection is fetched once and kept in memory.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Drops empty fields so "missing" looks the same as it did in the old JSON files.
const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== null && v !== undefined && v !== ''));

const run = async (query) => {
  const { data, error } = await query;
  if (error) throw error;
  return data;
};

const mapProgram = (r) =>
  clean({
    id: r.slug,
    n: r.n,
    kind: r.kind,
    group: r.group_name,
    tag: r.tag,
    title: r.title,
    cardTitle: r.card_title || r.title,
    unit: r.unit,
    place: r.place,
    duration: r.duration,
    schedule: r.schedule,
    language: r.language,
    price: r.price_text,
    feeStructured: r.fee_structured,
    feeCustomized: r.fee_customized,
    length: r.length_text,
    when: r.when_text,
    summary: r.summary,
    forWhom: r.for_whom,
    aim: r.aim,
    outcomes: r.outcomes,
    topics: r.topics,
    how: r.how_taught,
    sections: r.sections && Object.keys(r.sections).length ? r.sections : null,
    imagePath: r.image_path,
    brochurePath: r.brochure_path,
    featured: r.featured,
    seoTitle: r.seo_title,
    seoDescription: r.seo_description,
  });

const mapEvent = (r) => {
  const year = Number(r.event_date.slice(0, 4));
  const monthIndex = Number(r.event_date.slice(5, 7)) - 1;
  const day = Number(r.event_date.slice(8, 10));
  const month = MONTHS[monthIndex];
  const facts = r.side_facts || {};
  const dateLong = facts.dateLong || `${day} ${month} ${year}`;
  return clean({
    id: r.slug,
    title: r.title,
    day,
    month,
    year,
    type: r.type_label,
    location: r.location,
    summary: r.summary,
    photos: r.gallery_photo_count,
    imagePath: r.image_path,
    featured: r.featured,
    registrationEnabled: r.registration_enabled,
    date: r.event_date,
    endDate: r.end_date,
    seoTitle: r.seo_title,
    seoDescription: r.seo_description,
    detail: r.intro
      ? {
          meta: [facts.typeLong || r.type_label, dateLong, facts.locationLong || r.location],
          intro: r.intro,
          heading: r.heading,
          body: r.body,
          rows: [`${day} ${MONTHS_SHORT[monthIndex]} ${year}`, r.venue_short || '-', r.type_label || '-', facts.photosLabel || '-', facts.hostedBy || '-', facts.theme || '-'],
        }
      : null,
  });
};

const colomboDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Asia/Colombo', day: 'numeric', month: 'long', year: 'numeric' });

const mapNews = (r) =>
  clean({
    id: r.slug,
    date: r.date_label || colomboDate(r.publish_at),
    category: r.news_categories ? r.news_categories.name : undefined,
    author: r.author,
    title: r.title,
    titleMid: r.title_mid || r.title,
    titleShort: r.title_short || r.title,
    titleTiny: r.title_tiny || r.title_short || r.title,
    imagePath: r.image_path,
    featured: r.featured,
    seoTitle: r.seo_title,
    seoDescription: r.seo_description,
    article: {
      paras: (r.body || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
      heading: r.heading,
      body: r.extra_body,
      quote: r.quote,
      tags: r.tags || [],
    },
  });

const loaders = {
  programs: async () =>
    (await run(supabase.from('programs').select('*').eq('status', 'published').order('sort_order'))).map(mapProgram),

  staff: async () =>
    (await run(supabase.from('staff').select('*, departments(name, sort_order)').eq('status', 'published').order('sort_order'))).map((r) =>
      clean({
        fullName: r.full_name,
        displayName: r.display_name || r.full_name,
        rank: r.rank_title,
        department: r.departments ? r.departments.name : 'Other',
        departmentOrder: r.departments ? r.departments.sort_order : 99,
        role: r.role_title,
        qualifications: r.qualifications,
        photoPath: r.photo_path,
      })
    ),

  board: async () => {
    const rows = await run(supabase.from('board_members').select('*').eq('status', 'published').order('sort_order'));
    const pick = (group) => rows.filter((r) => r.group_name === group);
    return {
      board: pick('board').map((r) => clean({ fullName: r.full_name, displayName: r.display_name || r.full_name, rank: r.rank_title, photoPath: r.photo_path })),
      advisory: pick('advisory').map((r) => clean({ fullName: r.full_name, roleExact: r.role_title, photoPath: r.photo_path })),
      management: pick('management').map((r) => clean({ fullName: r.full_name, displayName: r.display_name || r.full_name, rank: r.rank_title, photoPath: r.photo_path })),
    };
  },

  events: async () => (await run(supabase.from('events').select('*').eq('status', 'published').order('event_date', { ascending: false }).order('sort_order'))).map(mapEvent),

  news: async () =>
    (await run(supabase.from('news_posts').select('*, news_categories(name)').eq('status', 'published').lte('publish_at', new Date().toISOString()).order('publish_at', { ascending: false }))).map(mapNews),

  testimonials: async () =>
    (await run(supabase.from('testimonials').select('*').eq('status', 'published').order('sort_order'))).map((r) =>
      clean({
        fullName: r.full_name,
        displayName: r.display_name || r.full_name,
        role: r.role,
        employer: r.employer,
        batch: r.batch,
        quoteShort: r.quote_short,
        quoteLong: r.quote_long,
        photoPath: r.photo_path,
      })
    ),

  faq: async () => (await run(supabase.from('faqs').select('*').eq('status', 'published').order('sort_order'))).map((r) => ({ question: r.question, answer: r.answer })),

  heroSlides: async () =>
    (await run(supabase.from('hero_slides').select('*').eq('status', 'published').order('sort_order'))).map((r) =>
      clean({
        imagePath: r.image_path,
        subtitle: r.eyebrow,
        line1: r.title_line1,
        line2: r.title_line2,
        accent: r.accent_word,
        text: r.text_lines,
        button: r.button_label ? { label: r.button_label, to: r.button_link || '/programs' } : null,
      })
    ),

  homeStats: async () =>
    (await run(supabase.from('home_stats').select('*').order('sort_order'))).map((r) => ({ countNum: r.value, countSubtext: r.suffix, countTitle: r.label })),

  facilities: async () =>
    (await run(supabase.from('facilities').select('*').eq('status', 'published').order('sort_order'))).map((r) =>
      clean({ icon: r.icon_class, title: r.title, to: r.link, description: r.description })
    ),

  projectCategories: async () =>
    (await run(supabase.from('project_categories').select('*').order('sort_order'))).map((r) => ({ id: r.id, slug: r.slug, name: r.name })),

  projects: async () => {
    const rows = await run(
      supabase
        .from('projects')
        .select('*, project_categories(name, slug), project_media(*)')
        .eq('status', 'published')
        .order('project_date', { ascending: false, nullsFirst: false })
        .order('sort_order')
    );
    return rows.map((r) =>
      clean({
        id: r.slug,
        title: r.title,
        summary: r.summary,
        body: r.body,
        partner: r.partner,
        date: r.project_date,
        category: r.project_categories ? r.project_categories.name : undefined,
        categorySlug: r.project_categories ? r.project_categories.slug : undefined,
        imagePath: r.image_path,
        videoUrl: r.video_url,
        documentPath: r.document_path,
        featured: r.featured,
        seoTitle: r.seo_title,
        seoDescription: r.seo_description,
        media: (r.project_media || [])
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((m) => clean({ id: m.id, kind: m.kind, path: m.storage_path, url: m.url, caption: m.caption })),
      })
    );
  },

  gallery: async () => {
    const rows = await run(supabase.from('gallery_albums').select('*, gallery_items(*)').eq('status', 'published').order('sort_order'));
    return rows.map((r) =>
      clean({
        id: r.slug,
        title: r.title,
        type: r.album_type,
        description: r.description,
        coverPath: r.cover_path,
        items: (r.gallery_items || [])
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((i) => clean({ id: i.id, kind: i.kind, path: i.storage_path, videoUrl: i.video_url, caption: i.caption })),
      })
    );
  },

  departments: async () =>
    (await run(supabase.from('departments').select('*').order('sort_order'))).map((r) =>
      clean({ id: r.id, slug: r.slug, name: r.name, email: r.email, phone: r.phone, description: r.description })
    ),

  // Never blocks a page: if the messages cannot be read, the page simply shows none.
  messages: async () => {
    try {
      const rows = await run(supabase.from('leadership_messages').select('*').eq('status', 'published'));
      const pick = (kind) => {
        const r = rows.find((x) => x.kind === kind);
        return r ? clean({ name: r.person_name, title: r.person_title, message: r.message, photoPath: r.photo_path }) : null;
      };
      return { chairman: pick('chairman'), viceChancellor: pick('vice-chancellor') };
    } catch (e) {
      return { chairman: null, viceChancellor: null };
    }
  },

  // Editable page text (the About page). Falls back to the built-in wording when nothing is saved.
  pages: async () => {
    try {
      const rows = await run(supabase.from('page_content').select('key, title, data').eq('status', 'published'));
      return Object.fromEntries(rows.map((r) => [r.key, r.data || {}]));
    } catch (e) {
      return {};
    }
  },

  seoPages: async () => {
    try {
      const rows = await run(supabase.from('seo_pages').select('*'));
      return Object.fromEntries(rows.map((r) => [r.path, { title: r.title, description: r.description, noindex: r.noindex }]));
    } catch (e) {
      return {};
    }
  },

  chairman: async () => {
    const rows = await run(supabase.from('leadership_messages').select('*').eq('kind', 'chairman').eq('status', 'published'));
    return rows[0]
      ? clean({ name: rows[0].person_name, title: rows[0].person_title, message: rows[0].message, photoPath: rows[0].photo_path })
      : null;
  },
};

const cache = new Map();

const load = (name) => {
  if (!loaders[name]) throw new Error(`Unknown collection: ${name}`);
  if (!cache.has(name)) {
    const entry = { data: undefined, promise: null };
    entry.promise = loaders[name]().then(
      (data) => {
        entry.data = data;
        return data;
      },
      (error) => {
        cache.delete(name);
        throw error;
      }
    );
    cache.set(name, entry);
  }
  return cache.get(name);
};

const isReady = (names) => names.every((n) => cache.has(n) && cache.get(n).data !== undefined);

// Usage: const { programs, loading, error, retry } = useCollections('programs', 'events');
export const useCollections = (...names) => {
  const key = names.join(',');
  const [state, setState] = useState({ loading: !isReady(names), error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    if (isReady(names)) {
      setState({ loading: false, error: null });
      return undefined;
    }
    setState({ loading: true, error: null });
    Promise.all(names.map((n) => load(n).promise)).then(
      () => alive && setState({ loading: false, error: null }),
      (error) => alive && setState({ loading: false, error })
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  const data = {};
  names.forEach((n) => {
    data[n] = cache.has(n) ? cache.get(n).data : undefined;
  });
  const loading = state.loading || !isReady(names);
  return { ...data, loading: state.error ? false : loading, error: state.error, retry };
};

// Date helpers shared by the pages.
export const monthShort = (monthName) => MONTHS_SHORT[MONTHS.indexOf(monthName)] || monthName;
